import useCommandFlowStore from "../../store/commandFlowStore";
import { getTranslationFunction } from "../../utils/core/translationUtils";
import { getStoreActions } from "../../utils/session/storeStateUtils";

const MARKER_RE = /§([a-zA-Z][\w-]*)\[([\s\S]*?)\]§/g;

// Parses §tag[content]§ markers in a string into an array of styled segments
// understood by TerminalOutput. Returns the original string untouched when no
// markers are present so plain output keeps the existing fast path.
export function parseMarkedText(input) {
  if (typeof input !== "string" || input.indexOf("§") === -1) return input;
  const segments = [];
  let lastIndex = 0;
  let match;
  let foundAny = false;
  MARKER_RE.lastIndex = 0;
  while ((match = MARKER_RE.exec(input)) !== null) {
    foundAny = true;
    if (match.index > lastIndex) {
      segments.push({ text: input.slice(lastIndex, match.index) });
    }
    segments.push({ text: match[2], color: match[1] });
    lastIndex = MARKER_RE.lastIndex;
  }
  if (!foundAny) return input;
  if (lastIndex < input.length) {
    segments.push({ text: input.slice(lastIndex) });
  }
  return segments;
}

// Strips §tag[content]§ markers, leaving only the content. Used when a string
// is consumed by something that does not understand segments (e.g. prompts).
export function stripMarkers(input) {
  if (typeof input !== "string" || input.indexOf("§") === -1) return input;
  return input.replace(MARKER_RE, (_m, _tag, content) => content);
}

/**
 * Processes and displays multi-line output with typing effect.
 *
 * NOTE: Always pushes the resolved output as a SINGLE entry, even when it
 * contains \n. CSS `white-space: pre-wrap` (set in TerminalOutput) renders
 * the embedded newlines as visible line breaks, while keeping the entire
 * intro as one entry means the typing animation runs once and a single
 * Enter/Space skips the whole block instead of needing one keypress per
 * line.
 */
export async function processMultiLineOutput(
  output,
  addOutputWithTypingEffect
) {
  const t = getTranslationFunction();
  const resolvedOutput = await Promise.resolve(output).catch(() =>
    t("errorOccurred")
  );

  const outputLines = [{ type: "output", text: parseMarkedText(resolvedOutput) }];
  await addOutputWithTypingEffect(outputLines);
}

/**
 * Processes and displays multi-line prompt with typing effect
 * @param {string|Promise<string>} prompt - The prompt to process
 * @param {Function} resolvePrompt - Function to resolve the prompt
 * @returns {Promise<string>} The final prompt line to display
 */
export async function processMultiLinePrompt(prompt, resolvePrompt) {
  const { addOutputWithTypingEffect } = getStoreActions();
  const resolvedPrompt = await resolvePrompt(prompt);

  // Check if prompt contains multiple lines and split them for delayed display
  if (typeof resolvedPrompt === "string" && resolvedPrompt.includes("\n")) {
    const lines = resolvedPrompt.split("\n");
    const lastLine = lines.pop(); // Remove the last line to use as prompt

    if (lines.length > 0) {
      // Join into a single output entry so one Enter skips the whole block.
      // CSS `white-space: pre-wrap` renders the embedded \n as line breaks.
      const outputLines = [
        { type: "output", text: parseMarkedText(lines.join("\n")) },
      ];
      await addOutputWithTypingEffect(outputLines);
    }

    return stripMarkers(lastLine);
  }

  return stripMarkers(resolvedPrompt);
}

/**
 * Displays resolved output with typing effect.
 *
 * Same single-entry approach as processMultiLineOutput — keep the whole
 * string as one output entry so the typing animation skips with a single
 * keypress instead of one per line.
 */
export async function displayResolvedOutput(resolvedOutput) {
  const { addOutputWithTypingEffect } = getStoreActions();
  const outputLines = [
    { type: "output", text: parseMarkedText(resolvedOutput) },
  ];
  await addOutputWithTypingEffect(outputLines);
}

/**
 * Sets prompt with typing effect, handling multi-line prompts
 * @param {string} promptText - The prompt text to display
 * @returns {Promise<void>}
 */
export async function setPromptWithTypingEffect(promptText) {
  const { setCurrentPromptWithTypingEffect } = getStoreActions();
  await setCurrentPromptWithTypingEffect(stripMarkers(promptText));
}
